import { ResponseCode } from '@/common/request/types';
import { useAppRouter } from '@/common/route/useAppRouter';
import { useCrudActions } from '@/common/view/useCrudActions';
import { useAppStore } from '@/modules/apps/store';
import { App } from '@/modules/apps/types';
import { ROUTE_NAME_ITEM } from '@/modules/item/router';
import { ROUTE_NAME_HOMEPAGE } from '@/modules/main/router';
import { itemService } from '@/services/item';
import { reactive } from 'vue';
import { useRoute } from 'vue-router';

export function useItemEdit() {
    const route = useRoute();
    const router = useAppRouter();
    const crudAction = useCrudActions();
    const appStore = useAppStore();

    const model = reactive({
        linkableApps: [],
        itemId: route.params.itemId,
        selectedAppId: null,
        startTime: null,
        endTime: null,
        canLink: false,
        canUnlink: false,
        canLinkNext: false,
        canUnlinkNext: false
    });

    const beforeFetch = crudAction.executeAction({
        request: () => itemService.item.getLinkableApp(model.itemId),
        onSuccess: ({ data }) => {
            model.linkableApps = data.appIds
                .map((appId: number) => appStore.getAppById(appId))
                .filter(Boolean)
                .map((app: App) => ({ label: app.name, value: app.id }));
        },
        onError: () => router.push({ name: ROUTE_NAME_HOMEPAGE })
    });

    const fetch = crudAction.executeAction({
        canExecute: () => model.selectedAppId != null && model.linkableApps.length > 0,
        request: () => itemService.item.getApp(model.itemId, model.selectedAppId),
        onSuccess: ({ data }) => {
            model.canLink = false;
            model.canUnlink = false;
            model.canLinkNext = false;
            model.canUnlinkNext = false;
            model.startTime = null;
            model.endTime = null;

            if (data.startTime) model.startTime = new Date(data.startTime).toString();

            if (data.endTime) {
                const now = new Date().getTime();
                model.canUnlink = data.endTime > now;
                model.endTime = new Date(data.endTime).toString();
            } else {
                model.canUnlink = true;
                model.canUnlinkNext = true;
            }
        },
        onError: ({ code }) => {
            if (code === ResponseCode.NothingFound || code === ResponseCode.Error) {
                model.canLink = true;
                model.canUnlink = false;
                model.canLinkNext = true;
                model.canUnlinkNext = false;
                model.startTime = null;
                model.endTime = null;
            } else {
                router.push({ name: ROUTE_NAME_ITEM.SYSTEM.INDEX });
            }
        }
    });

    const actions = {
        link: crudAction.confirmAndExecute({
            confirmMessage: 'Are you sure you want to link this app?',
            request: () => itemService.item.addForApp(model.itemId, model.selectedAppId),
            onSuccess: fetch
        }),
        unlink: crudAction.confirmAndExecute({
            confirmMessage: 'Are you sure you want to unlink this app?',
            request: () => itemService.item.removeFromApp(model.itemId, model.selectedAppId),
            onSuccess: fetch
        }),
        linkNext: crudAction.confirmAndExecute({
            confirmMessage: 'Are you sure you want to link in next cycle?',
            request: () => itemService.item.addForAppNext(model.itemId, model.selectedAppId),
            onSuccess: fetch
        }),
        unlinkNext: crudAction.confirmAndExecute({
            confirmMessage: 'Are you sure you want to unlink in next cycle?',
            request: () => itemService.item.removeFromAppNext(model.itemId, model.selectedAppId),
            onSuccess: fetch
        })
    };

    return { model, beforeFetch, fetch, actions };
}
